import { useState } from 'react';
import RadarChart from './RadarChart';
import { PILLARS } from '../utils/scoring';

const COMP_COLORS = [
  { bg: 'bg-red-500/20', text: 'text-red-400', hex: '#f87171' },
  { bg: 'bg-blue-500/20', text: 'text-blue-400', hex: '#60a5fa' },
  { bg: 'bg-purple-500/20', text: 'text-purple-400', hex: '#c084fc' },
  { bg: 'bg-emerald-500/20', text: 'text-emerald-400', hex: '#34d399' },
  { bg: 'bg-orange-500/20', text: 'text-orange-400', hex: '#fb923c' },
  { bg: 'bg-cyan-500/20', text: 'text-cyan-400', hex: '#22d3ee' },
  { bg: 'bg-pink-500/20', text: 'text-pink-400', hex: '#f472b6' },
  { bg: 'bg-yellow-500/20', text: 'text-yellow-400', hex: '#facc15' },
  { bg: 'bg-lime-500/20', text: 'text-lime-400', hex: '#a3e635' },
  { bg: 'bg-rose-500/20', text: 'text-rose-400', hex: '#fb7185' },
];

export default function CompetitiveRadar({ scores, marketData, competitors = [] }) {
  const comps = (Array.isArray(marketData?.competitors) ? marketData.competitors : Array.isArray(competitors) ? competitors : []).slice(0, 10);

  // Build competitor scores — each gets a fixed originalIndex for consistent colors
  const allComps = comps.map((c, i) => ({
    ...c,
    originalIndex: i,
    color: COMP_COLORS[i] || COMP_COLORS[0],
    enabled: true,
    scores: buildCompScoresFromData(scores, c),
  }));

  const [disabledNames, setDisabledNames] = useState(new Set());

  const toggleComp = (name) => {
    setDisabledNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const enabledComps = allComps.filter(c => !disabledNames.has(c.name));

  return (
    <div className="card p-6">
      <div className="mb-4">
        <h3 className="font-display text-lg text-white">Competitive Position</h3>
        <p className="text-xs text-neutral-500 mt-0.5">Your scores vs local competitors — toggle each on/off</p>
      </div>

      {allComps.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {allComps.map((comp) => {
            const isEnabled = !disabledNames.has(comp.name);
            return (
            <button
              key={comp.originalIndex}
              onClick={() => toggleComp(comp.name)}
              className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full font-medium transition-all border ${
                isEnabled
                  ? `${comp.color.bg} ${comp.color.text} border-transparent`
                  : 'bg-neutral-800/30 text-neutral-600 border-neutral-700/50 line-through'
              }`}
            >
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: isEnabled ? comp.color.hex : '#555' }} />
              {comp.name}
              {comp.reviewScore ? ` (${comp.reviewScore}★)` : ''}
            </button>
            );
          })}
        </div>
      )}

      <RadarChart scores={scores} competitors={enabledComps} showBenchmark={false} />

      {allComps.length === 0 && (
        <p className="text-neutral-600 text-sm text-center py-4">Run Market Research (Steps 1-2) to populate competitors</p>
      )}
    </div>
  );
}

// Build pillar scores for a competitor using real data + context
function buildCompScoresFromData(myScores, comp) {
  if (comp.scores) return comp.scores;

  const pillars = Object.keys(myScores);
  const estScores = {};
  const reviewScore = comp.reviewScore || 0;
  const baseScore = reviewScore > 0 ? reviewScore * 2 : 5;
  const compName = (comp.name || '').length; // Use name length as a seed for variation

  pillars.forEach((pid, idx) => {
    // Per-competitor, per-pillar deterministic offset so lines look different
    const offset = ((compName * 3 + idx * 7) % 20 - 10) * 0.15;
    // Bonus from strength/weakness text matching pillar name
    let contextBonus = 0;
    const pillarWords = pid.split('_');
    if (comp.strengths) {
      const s = comp.strengths.toLowerCase();
      if (pillarWords.some(w => s.includes(w))) contextBonus = 0.6;
    }
    if (comp.weaknesses) {
      const w = comp.weaknesses.toLowerCase();
      if (pillarWords.some(word => w.includes(word))) contextBonus = -0.6;
    }
    const score = Math.max(1, Math.min(10, baseScore + offset + contextBonus));
    estScores[pid] = { score: Math.round(score * 10) / 10 };
  });

  return estScores;
}
